import React, { FC, useEffect, useState } from "react";
import {
    Box,
    Button,
    Stack,
    SxProps,
    Tooltip,
    Typography,
} from "@mui/material";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import UploadIcon from "@mui/icons-material/Upload";
import {
    IrecordRtcUtils,
    RECORD_RTC_RECORDER_STATES,
    recordRtcUtils,
} from "../../../util/recordRtcUtils";
import { noop } from "lodash";
import { useTheme,Theme } from '@mui/material/styles';

export interface IRecorderInstance {
    RecordPlayerInstance: IrecordRtcUtils;
    play: () => void;
    pause: () => void;
    resume: () => void;
    stop: () => void;
    reset: () => void;
}

interface IRecordPlayerProps {
    onStart?: Function;
    onPause?: Function;
    onResume?: Function;
    onStop?: Function;
    onReset?: Function;
    onUpload?: Function;
    onStateChange?: Function;
    sx?: SxProps,
    className?: string;
    onRecorderInitialization?: (recorderInstance: any) => void;
}

const ACTION_BUTTON_STYLES: SxProps = {
    minWidth: "auto",
    padding: "7.5px",
    borderRadius: "50%",
};

export const RECORDING_INDICATOR_STYLES = (theme: Theme)=> ({
    height: "10px",
    width: "10px",
    borderRadius: "50%",
    backgroundColor: theme.custom.recordingIndicatorColor,
    animation: "pulse 1s infinite",
    "@keyframes pulse": {
        "0%": {
            boxShadow: `0 0 0 0px rgba(229, 83, 83, 0.2)`,
        },
        "100%": {
            boxShadow: "0 0 0 12px rgba(229, 83, 83, 0)",
        },
    },
});

const RecordPlayer: FC<IRecordPlayerProps> = (props) => {
    const {
        onStart,
        onPause,
        onResume,
        onReset,
        onStop,
        onUpload,
        onStateChange,
        sx,
        className,
        onRecorderInitialization
    } = props;
    const [recordingDuration, setRecordingDuration] =
        useState<string>("00 : 00 : 00");
    const [recorderState, setRecorderState] = useState<string>(
        RECORD_RTC_RECORDER_STATES.INACTIVE
    );
    const [RecordPlayerUtils, setRecordPlayerUtils] =
        useState<IrecordRtcUtils>(null);
    const theme = useTheme();
    const disableStartButton =
        recorderState !== RECORD_RTC_RECORDER_STATES.INACTIVE &&
        recorderState !== RECORD_RTC_RECORDER_STATES.STOPPED;
    const disablePauseButton =
        recorderState !== RECORD_RTC_RECORDER_STATES.RECORDING;
    const disableResumetButton =
        recorderState !== RECORD_RTC_RECORDER_STATES.PAUSED;
    const disableStopButton =
        recorderState === RECORD_RTC_RECORDER_STATES.INACTIVE ||
        recorderState === RECORD_RTC_RECORDER_STATES.STOPPED;

    const getRecorderState = () => {
        setRecorderState(RecordPlayerUtils.getState());
    };

    const onStartHandler = () => {
        RecordPlayerUtils.start(() => {
            getRecorderState();
            onStart(RecordPlayerUtils);
        });
    };

    const onPauseHandler = () => {
        RecordPlayerUtils.pause(() => {
            getRecorderState();
            onPause(RecordPlayerUtils);
        });
    };

    const onResumeHandler = () => {
        RecordPlayerUtils.resume(() => {
            getRecorderState();
            onResume(RecordPlayerUtils);
        });
    };

    const onStopHandler = () => {
        if (recorderState === RECORD_RTC_RECORDER_STATES.PAUSED) {
            onResumeHandler();
        }

        RecordPlayerUtils.stop(() => {
            getRecorderState();
            onStop(RecordPlayerUtils);
        });
    };

    const onResetHandler = () => {
        RecordPlayerUtils.reset(() => {
            getRecorderState();
            onReset(RecordPlayerUtils);
        });
    }

    const onRecordingUploadHandler = () => {
        onUpload(RecordPlayerUtils);
    };

    const playButtonHandler = () => {
        if (!disableStartButton) {
            return onStartHandler();
        }

        if (!disablePauseButton) {
            return onPauseHandler();
        }

        if (!disableResumetButton) {
            return onResumeHandler();
        }
    };

    const getPlayButtonTooltipText = () => {
        if (!disablePauseButton) {
            return "Pause";
        }
        if (!disableResumetButton) {
            return "Resume";
        }

        return "Record";
    };

    useEffect(() => {
        onStateChange(recorderState);
    }, [recorderState]);

    useEffect(() => {
        return () => {
            if (!!RecordPlayerUtils) {
                RecordPlayerUtils.destroy();
            }
        };
    }, [RecordPlayerUtils]);

    useEffect(() => {
        setRecordPlayerUtils(
            recordRtcUtils({
                recordrtcOptions: {
                    type: "audio",
                    timeSlice: 3000, // Force recordrtc to create a seperate blob every 3 minutes
                    mimeType: "audio/webm",
                },
                includeVideo: false,
                onDurationChange: setRecordingDuration,
            })
        );
    }, []);

    useEffect(() => {
        const playerInstance: IRecorderInstance = {
            RecordPlayerInstance: RecordPlayerUtils,
            play: onStartHandler,
            pause: onPauseHandler,
            resume: onResumeHandler,
            stop: onStopHandler,
            reset: onResetHandler,
        };

        onRecorderInitialization(playerInstance);
    }, [RecordPlayerUtils])

    return (
        <Stack
            className={className}
            sx={{
                borderRadius: "4px",
                border: `1px solid ${theme.custom.borderGrey}`,
                padding: "16px",
                ...sx,
            }}
            spacing={2}
        >
            <Stack
                direction="row"
                spacing={1}
            >
                <Tooltip title={getPlayButtonTooltipText()}>
                    <Button
                        sx={ACTION_BUTTON_STYLES}
                        onClick={playButtonHandler}
                        variant="contained"
                    >
                        {!disableStartButton && <MicOutlinedIcon />}
                        {!disablePauseButton && <PauseIcon />}
                        {!disableResumetButton && <PlayArrowIcon />}
                    </Button>
                </Tooltip>
                <Tooltip title="Stop">
                    <Box>
                        <Button
                            sx={ACTION_BUTTON_STYLES}
                            onClick={onStopHandler}
                            disabled={disableStopButton}
                            variant="outlined"
                        >
                            <StopIcon />
                        </Button>
                    </Box>
                </Tooltip>
                {recorderState === RECORD_RTC_RECORDER_STATES.STOPPED && (
                    <Tooltip title="Upload Recording">
                        <Button
                            sx={ACTION_BUTTON_STYLES}
                            onClick={onRecordingUploadHandler}
                            variant="outlined"
                        >
                            <UploadIcon />
                        </Button>
                    </Tooltip>
                )}
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        flexGrow: 1,
                        justifyContent: "flex-end",
                    }}
                    spacing={1}
                >
                    {recorderState === RECORD_RTC_RECORDER_STATES.RECORDING && (
                        <Box sx={RECORDING_INDICATOR_STYLES(theme)} />
                    )}
                    <Typography sx={{ minWidth: "74px" }}>{recordingDuration}</Typography>
                </Stack>
            </Stack>
        </Stack>
    );
};

RecordPlayer.defaultProps = {
    onStart: noop,
    onPause: noop,
    onResume: noop,
    onStop: noop,
    onReset: noop,
    onUpload: noop,
    onStateChange: noop,
    sx: {},
    className: "",
};

export default RecordPlayer;
